import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { userNotificationService } from "../services/userNotificationService";
import { userNotificationDisplayId } from "./UserNotificationCommonStore";
import { UserNotification } from "../../../api/graphql";
import { userDisplayId } from "../../user/store/UserCommonStore";
import { NotificationType } from "../../../api/graphql";
import { NotificationPriority } from "../../../api/graphql";
import { userService } from "../../user/services/userService";
import { InitializeParams } from "../../../store/types";

export class UserNotificationViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = userNotificationService;
  userNotificationInstance: UserNotification | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  typeValue: NotificationType = "" as NotificationType;
  priorityValue: NotificationPriority | "" = "" as NotificationPriority | "";
  titleValue: string = "";
  bodyValue: string = "";
  actorUserService = userService;
  actorUserSearchValue: string = "";
  actorUserIdValue: string = "";
  actorUserSelectedValue: { displayId: string; id: string } | null = null;
  objectTypeValue: string = "";
  objectIdValue: string = "";
  actionUrlValue: string = "";
  readAtValue: Date | null = null;
  notificationCreatedAtValue: Date | null = null;
  archivedAtValue: Date | null = null;
  metadataValue: string = "{}";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadUserNotificationById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.userNotificationOne({ id });
    this.userNotificationInstance = result.userNotificationOne;
    if (this.userNotificationInstance) {
      this.loadValues(this.userNotificationInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.userNotificationInstance) {
      return userNotificationDisplayId(
        this.userNotificationInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.UserNotificationSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "actorUser") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.userOne({ id: params.returnData.resultId });
        const user = result.userOne;
        this.actorUserSelectedValue = user;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getTypeOptions() {
    return this.langStore.NotificationTypeOptions;
  }

  getPriorityOptions() {
    return this.langStore.NotificationPriorityOptions;
  }

  get actorUserValue() {
    if (this.actorUserSelectedValue) {
      return {
        id: this.actorUserSelectedValue.id,
        displayId: userDisplayId(this.actorUserSelectedValue, this.langStore),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.userNotificationInstance = null;
    this.typeValue = "" as NotificationType;
    this.priorityValue = "" as NotificationPriority | "";
    this.titleValue = "";
    this.bodyValue = "";
    this.actorUserSearchValue = "";
    this.actorUserIdValue = "";
    this.objectTypeValue = "";
    this.objectIdValue = "";
    this.actionUrlValue = "";
    this.readAtValue = null;
    this.notificationCreatedAtValue = null;
    this.archivedAtValue = null;
    this.metadataValue = "{}";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelUserNotification() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editUserNotification() {
    const id = this.userNotificationInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/userNotification/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printUserNotification(handlePrint: () => void): void {
    handlePrint();
  }

  *closeUserNotification(): any {
    yield this.close();
  }

  loadValues(instance: UserNotification) {
    this.idValue = instance.id;
    this.typeValue = instance.type;
    this.priorityValue = instance.priority || ("" as NotificationPriority | "");
    this.titleValue = instance.title;
    this.bodyValue = instance.body || "";
    this.actorUserSelectedValue = instance.actorUser
      ? instance.actorUser
      : null;
    this.objectTypeValue = instance.objectType || "";
    this.objectIdValue = instance.objectId || "";
    this.actionUrlValue = instance.actionUrl || "";
    this.readAtValue = instance.readAt ? new Date(instance.readAt) : null;
    this.notificationCreatedAtValue = instance.notificationCreatedAt
      ? new Date(instance.notificationCreatedAt)
      : null;
    this.archivedAtValue = instance.archivedAt
      ? new Date(instance.archivedAt)
      : null;
    this.metadataValue = instance.metadata || "";
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadUserNotificationById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
